import { uid } from "@dhx/ts-common/core";
import { awaitRedraw } from "@dhx/ts-common/dom";
import { IEventSystem } from "@dhx/ts-common/events";
import { Id } from "@dhx/ts-common/types";
import { ShapesCollection } from "../ShapesCollection";
import { ActionDirection, ActionValidate, ICellType, ISwimlaneConfig } from "../types";

export enum CellManagerEvents {
	beforeCellsAdd = "beforeCellsAdd",
	afterCellsAdd = "afterCellsAdd",
	beforeCellsMove = "beforeCellsMove",
	afterCellsMove = "afterCellsMove",
	beforeCellsRemove = "beforeCellsRemove",
	afterCellsRemove = "afterCellsRemove",
	beforeCellsValidation = "beforeCellsValidation",
	afterCellsValidation = "afterCellsValidation",
}

export interface ICellManagerHandlersMap {
	[key: string]: (...args: any[]) => any;

	[CellManagerEvents.beforeCellsAdd]: (swimlaneId: Id) => boolean | void;
	[CellManagerEvents.afterCellsAdd]: (swimlaneId: Id) => void;
	[CellManagerEvents.beforeCellsMove]: (swimlaneId: Id) => boolean | void;
	[CellManagerEvents.afterCellsMove]: (swimlaneId: Id) => void;
	[CellManagerEvents.beforeCellsRemove]: (swimlaneId: Id) => boolean | void;
	[CellManagerEvents.afterCellsRemove]: (swimlaneId: Id) => void;
	[CellManagerEvents.beforeCellsValidation]: (swimlaneId: Id, action: ActionValidate) => boolean | void;
	[CellManagerEvents.afterCellsValidation]: (
		swimlaneId: Id,
		validate: boolean,
		action: ActionValidate
	) => void;
}

export interface ICellManager {
	data: ShapesCollection;
	swimlane: ISwimlaneConfig | null;

	setSwimlane(id: Id): boolean;
	resetSwimlane(): void;

	add(cellIndex: number, dir: ActionDirection, unstrict?: boolean): void;
	move(cellIndex: number, dir: ActionDirection, unstrict?: boolean): void;
	remove(cellIndex: number, type: ICellType, unstrict?: boolean): void;
	validation(cellIndex: number, dir: ActionDirection, action: ActionValidate): boolean;

	getSubHeaderCellIndex(subheaderId: string): number;
	getSubHeaderCellId(subheaderId: string): Id;
	getSubHeaderType(subheaderId: string): ICellType | undefined;

	getCellIndex(cellId: Id, type: ICellType): number | undefined;
	getCellId(cellIndex: number, type: ICellType): Id | undefined;
}

export class CellManager implements ICellManager {
	events: IEventSystem<CellManagerEvents, ICellManagerHandlersMap>;
	data: ShapesCollection;
	swimlane: ISwimlaneConfig;

	constructor(data: ShapesCollection, events) {
		this.events = events;
		this.data = data;
		this.swimlane = null;
	}

	public setSwimlane(id: Id): boolean {
		const swimlane = this.data.getItem(id) as ISwimlaneConfig;

		if (swimlane?.$item?.getBaseType() === "swimlane") {
			this.swimlane = swimlane;
			return true;
		}
		return false;
	}
	public resetSwimlane(): void {
		awaitRedraw().then(() => {
			this.swimlane = null;
		});
	}

	public add(cellIndex: number, dir: ActionDirection, unstrict?: boolean): void {
		awaitRedraw().then(() => {
			const type = this.getDirectionType(dir);
			const uniqueLayout = this.getUniqueLayout();
			const maxIndex = type === "col" ? uniqueLayout[0]?.length - 1 : uniqueLayout?.length - 1;
			const isIndexCorrect = cellIndex >= 0 && cellIndex <= maxIndex;

			if (
				!isIndexCorrect ||
				!this.events.fire(CellManagerEvents.beforeCellsAdd, [this.swimlane.id]) ||
				(!unstrict && !this.validation(cellIndex, dir, "add"))
			) {
				return;
			}

			const layout = this.swimlane.layout;

			const targetIndex = cellIndex;
			const targetId = type === "col" ? layout[0][targetIndex] : layout[targetIndex][0];
			const targetItem = this.data.getItem(targetId);
			const placeIndexOffset = type === "col" ? targetItem?.$colCount : targetItem?.$rowCount;

			const subHeader = this.swimlane[type === "col" ? "subHeaderCols" : "subHeaderRows"];
			const isSubHeaderEnable = subHeader?.enable && subHeader?.headers?.length;
			let headers;
			let headerIndex;

			if (isSubHeaderEnable) {
				headers = this.swimlane[type === "col" ? "subHeaderCols" : "subHeaderRows"].headers;
				headerIndex = this.getSubHeaderIndex(this.getCellId(cellIndex, type), type);
			}

			let measureOffset = +Infinity;
			let placeIndex = targetIndex;

			if (dir === "right" || dir === "down") {
				placeIndex += placeIndexOffset;
				isSubHeaderEnable && headerIndex++;
			}
			if (placeIndex < 0) placeIndex = 0;

			const newCells = {};
			const placed = {};
			const mutable = {};

			for (let i = 0; i < layout.length; i++) {
				const rows = layout[i];
				for (let k = 0; k < rows.length; k++) {
					if (type === "col") {
						const cell = this.data.getItem(rows[k]);
						if (cell && measureOffset > cell.width) measureOffset = cell.width;

						if (k === targetIndex) {
							if (placed[cell.id]) {
								layout[i].splice(placeIndex, 0, placed[cell.id]);
							} else {
								const id = uid();
								newCells[id] = { id, type: "$sgroup" };
								layout[i].splice(placeIndex, 0, id);

								placed[cell.id] = id;
							}
						}
					}
					if (type === "row") {
						const cell = this.data.getItem(rows[k]);
						if (cell && measureOffset > cell.height) measureOffset = cell.height;

						if (i === targetIndex) {
							const id = uid();

							if (k === 0) {
								layout.splice(placeIndex, 0, []);
							}

							if (placed[cell.id]) {
								layout[placeIndex][k] = placed[cell.id];
							} else {
								newCells[id] = { id, type: "$sgroup" };
								layout[placeIndex][k] = id;

								placed[cell.id] = id;
							}
						}
					}
				}
			}

			for (let i = 0; i < layout.length; i++) {
				const rows = layout[i];
				for (let k = 0; k < rows.length; k++) {
					if (type === "col") {
						const cellId = rows[k];
						if (k > placeIndex && !mutable[cellId]) {
							this.data.eachChild(cellId, item => {
								item.x += measureOffset;
							});
							mutable[cellId] = true;
						}
					}
					if (type === "row") {
						const cellId = rows[k];
						if (i > placeIndex && !mutable[cellId]) {
							this.data.eachChild(cellId, item => {
								item.y += measureOffset;
							});
							mutable[cellId] = true;
						}
					}
				}
			}

			isSubHeaderEnable && headers.splice(headerIndex, 0, { text: "Untitled", id: uid() });

			const config: any = { layout };
			if (type === "col") {
				config.width = this.swimlane.width + measureOffset;
			} else {
				config.height = this.swimlane.height + measureOffset;
			}

			this.data.add(Object.values(newCells));
			this.data.update(this.swimlane.id, config);

			this.events.fire(CellManagerEvents.afterCellsAdd, [this.swimlane.id]);
		});
	}
	public move(cellIndex: number, dir: ActionDirection, unstrict?: boolean): void {
		awaitRedraw().then(() => {
			const type = this.getDirectionType(dir);
			const uniqueLayout = this.getUniqueLayout();
			const maxIndex = type === "col" ? uniqueLayout[0]?.length - 1 : uniqueLayout?.length - 1;
			const isIndexCorrect = cellIndex >= 0 && cellIndex <= maxIndex;

			if (
				!isIndexCorrect ||
				!this.events.fire(CellManagerEvents.beforeCellsMove, [this.swimlane.id]) ||
				(!unstrict && !this.validation(cellIndex, dir, "move"))
			) {
				return;
			}

			const layout = this.swimlane.layout;

			const subHeader = this.swimlane[type === "col" ? "subHeaderCols" : "subHeaderRows"];
			const isSubHeaderEnable = subHeader?.enable && subHeader?.headers?.length;
			let headers;
			let headerIndex;

			if (isSubHeaderEnable) {
				headers = this.swimlane[type === "col" ? "subHeaderCols" : "subHeaderRows"].headers;
				headerIndex = this.getSubHeaderIndex(this.getCellId(cellIndex, type), type);
			}

			const targetIndex = cellIndex;
			const targetId = type === "col" ? layout[0][targetIndex] : layout[targetIndex][0];
			const firstTarget = this.data.getItem(targetId);

			const placedHeaderIndex = dir === "right" || dir === "down" ? headerIndex + 1 : headerIndex - 1;
			const placedHeaderId = this.swimlane[type === "col" ? "subHeaderCols" : "subHeaderRows"].headers[
				placedHeaderIndex
			].id;
			const firstPlaced = this.data.getItem(this.getSubHeaderCellId(placedHeaderId));

			const targetIndexOffset = type === "col" ? firstTarget?.$colCount : firstTarget?.$rowCount;
			const placedIndexOffset = type === "col" ? firstPlaced?.$colCount : firstPlaced?.$rowCount;

			let placedIndex =
				dir === "left" || dir === "up"
					? targetIndex - placedIndexOffset
					: targetIndex + targetIndexOffset;

			const mutable = {};
			const moved = [];

			for (let i = 0; i < layout.length; i++) {
				const rows = layout[i];
				for (let k = 0; k < rows.length; k++) {
					if (type === "col" && k === targetIndex) {
						const relocableId = layout[i][targetIndex];
						const placedId = layout[i][placedIndex];

						const relocable = this.data.getItem(relocableId);
						const placed = this.data.getItem(placedId);

						if (!mutable[relocableId]) {
							this.data.eachChild(relocableId, item => {
								if (dir === "left") {
									item.x -= placed.width;
								} else {
									item.x += placed.width;
								}
							});
							mutable[relocableId] = true;
						}
						if (!mutable[placedId]) {
							this.data.eachChild(placedId, item => {
								if (dir === "left") {
									item.x += relocable.width;
								} else {
									item.x -= relocable.width;
								}
							});
							mutable[placedId] = true;
						}

						if (i === 0 && isSubHeaderEnable) {
							[headers[headerIndex], headers[placedHeaderIndex]] = [
								headers[placedHeaderIndex],
								headers[headerIndex],
							];
						}

						moved[i] = layout[i].splice(k, targetIndexOffset);
					}
					if (type === "row" && i === targetIndex) {
						const relocableId = layout[targetIndex][k];
						const placedId = layout[placedIndex][k];

						const relocable = this.data.getItem(relocableId);
						const placed = this.data.getItem(placedId);

						if (!mutable[relocableId]) {
							this.data.eachChild(relocableId, item => {
								if (dir === "up") {
									item.y -= placed.height;
								} else {
									item.y += placed.height;
								}
							});
							mutable[relocableId] = true;
						}
						if (!mutable[placedId]) {
							this.data.eachChild(placedId, item => {
								if (dir === "up") {
									item.y += relocable.height;
								} else {
									item.y -= relocable.height;
								}
							});
							mutable[placedId] = true;
						}

						if (k === rows.length - 1) {
							if (isSubHeaderEnable) {
								[headers[headerIndex], headers[placedHeaderIndex]] = [
									headers[placedHeaderIndex],
									headers[headerIndex],
								];
							}
							moved.push(layout.splice(i, targetIndexOffset));
						}
					}
				}
			}

			if (dir === "right" || dir === "down") {
				placedIndex = targetIndex + placedIndexOffset;
			}

			for (let i = 0; i < layout.length; i++) {
				const rows = layout[i];
				for (let k = 0; k < rows.length; k++) {
					if ((dir === "left" && k === placedIndex) || (dir === "right" && k === targetIndex)) {
						layout[i].splice(placedIndex, 0, ...moved[i]);
					}
					if (
						((dir === "up" && i === placedIndex) || (dir === "down" && i === targetIndex)) &&
						k === 0
					) {
						layout.splice(placedIndex, 0, ...moved[0]);
					}
				}
			}

			this.data.update(this.swimlane.id, this.swimlane);

			this.events.fire(CellManagerEvents.afterCellsMove, [this.swimlane.id]);
		});
	}
	public remove(cellIndex: number, type: ICellType, unstrict?: boolean): void {
		awaitRedraw().then(() => {
			const cellDirection = type === "col" ? "left" : "up";
			const uniqueLayout = this.getUniqueLayout();
			const maxIndex = type === "col" ? uniqueLayout[0]?.length - 1 : uniqueLayout?.length - 1;
			const isIndexCorrect = cellIndex >= 0 && cellIndex <= maxIndex;

			if (
				!isIndexCorrect ||
				!this.events.fire(CellManagerEvents.beforeCellsRemove, [this.swimlane.id]) ||
				(!unstrict && !this.validation(cellIndex, cellDirection, "remove"))
			) {
				return;
			}

			const layout = this.swimlane.layout;

			const subHeader = this.swimlane[type === "col" ? "subHeaderCols" : "subHeaderRows"];
			const isSubHeaderEnable = subHeader?.enable && subHeader?.headers?.length;
			let headers;
			let headerIndex;

			if (isSubHeaderEnable) {
				headers = this.swimlane[type === "col" ? "subHeaderCols" : "subHeaderRows"].headers;
				headerIndex = this.getSubHeaderIndex(this.getCellId(cellIndex, type), type);
			}

			const targetIndex = cellIndex;
			const targetId = type === "col" ? layout[0][targetIndex] : layout[targetIndex][0];
			const targetItem = this.data.getItem(targetId);

			const removable = {};
			const mutable = {};
			const reformed = [];

			for (let i = 0; i < layout.length; i++) {
				const rows = layout[i];
				for (let k = 0; k < rows.length; k++) {
					const currentId = layout[i][k];
					if ((type === "col" && k === targetIndex) || (type === "row" && i === targetIndex)) {
						removable[currentId] = true;
						if (
							isSubHeaderEnable &&
							((type === "col" && i === 0) || (type === "row" && k === 0))
						) {
							headers.splice(headerIndex, 1);
						}
					} else if (!removable[currentId]) {
						if (!reformed[i]) {
							reformed[i] = [currentId];
						} else {
							reformed[i].push(currentId);
						}
					}
					if (!removable[currentId] && !mutable[currentId]) {
						if (type === "col" && targetIndex < k) {
							this.data.eachChild(currentId, item => {
								item.x -= targetItem.width;
							});
							mutable[currentId] = true;
						} else if (type === "row" && targetIndex < i) {
							this.data.eachChild(currentId, item => {
								item.y -= targetItem.height;
							});
							mutable[currentId] = true;
						}
					}
				}
			}

			const config: any = {
				layout: reformed.filter(i => i),
			};
			if (type === "col") {
				config.width = this.swimlane.width - targetItem.width;
			} else {
				config.height = this.swimlane.height - targetItem.height;
			}

			this.data.remove(Object.keys(removable));
			this.data.update(this.swimlane.id, config);

			this.events.fire(CellManagerEvents.afterCellsRemove, [this.swimlane.id]);
		});
	}
	public validation(cellIndex: number, dir: ActionDirection, action: ActionValidate): boolean {
		const type = this.getDirectionType(dir);
		const uniqueLayout = this.getUniqueLayout();
		const maxIndex = type === "col" ? uniqueLayout[0]?.length - 1 : uniqueLayout?.length - 1;
		const isIndexCorrect = cellIndex >= 0 && cellIndex <= maxIndex;

		if (
			!isIndexCorrect ||
			!this.events.fire(CellManagerEvents.beforeCellsValidation, [this.swimlane.id, action])
		) {
			return;
		}

		if (maxIndex === 0 && action !== "add") {
			this.events.fire(CellManagerEvents.afterCellsValidation, [this.swimlane.id, false, action]);
			return false;
		}

		const layout = this.swimlane.layout;

		let targetIndex = cellIndex;
		let targetCount;
		let validFirstStep = true;

		if (targetIndex >= 0) {
			for (let i = 0; i < layout.length; i++) {
				const rows = layout[i];
				for (let k = 0; k < rows.length; k++) {
					const targetId = type === "col" ? rows[targetIndex] : layout[targetIndex][k];

					if (type === "col" && k === targetIndex) {
						const colCount = this.data.getItem(targetId).$colCount;
						if (i === 0) {
							targetCount = colCount;
						} else if (targetCount !== colCount) {
							if (action === "remove" || action === "move") {
								this.events.fire(CellManagerEvents.afterCellsValidation, [
									this.swimlane.id,
									false,
									action,
								]);
								return false;
							}
							validFirstStep = false;
						}
					}
					if (type === "row" && i === targetIndex) {
						const rowCount = this.data.getItem(targetId).$rowCount;
						if (k === 0) {
							targetCount = rowCount;
						} else if (targetCount !== rowCount) {
							if (action === "remove" || action === "move") {
								this.events.fire(CellManagerEvents.afterCellsValidation, [
									this.swimlane.id,
									false,
									action,
								]);
								return false;
							}
							validFirstStep = false;
						}
					}
				}
			}
		}

		if ((dir && !validFirstStep) || action === "move") {
			const targetCell = type === "col" ? layout[0][targetIndex] : layout[targetIndex][0];
			const targetItem = this.data.getItem(targetCell);
			const offset = type === "col" ? targetItem?.$colCount : targetItem?.$rowCount;

			if (dir === "right" || dir === "down") {
				targetIndex += offset;
			} else {
				targetIndex -= 1;
			}

			if (targetIndex >= 0 && !(type === "row" && targetIndex > layout.length - 1)) {
				for (let i = 0; i < layout.length; i++) {
					const rows = layout[i];
					for (let k = 0; k < rows.length; k++) {
						const currentCell = type === "col" ? rows[targetIndex] : layout[targetIndex][k];

						if (type === "col" && k === targetIndex) {
							const colCount = this.data.getItem(currentCell).$colCount;
							if (i === 0) {
								targetCount = colCount;
							} else if (targetCount !== colCount) {
								this.events.fire(CellManagerEvents.afterCellsValidation, [
									this.swimlane.id,
									false,
									action,
								]);
								return false;
							}
						}
						if (type === "row" && i === targetIndex) {
							const rowCount = this.data.getItem(currentCell).$rowCount;
							if (k === 0) {
								targetCount = rowCount;
							} else if (targetCount !== rowCount) {
								this.events.fire(CellManagerEvents.afterCellsValidation, [
									this.swimlane.id,
									false,
									action,
								]);
								return false;
							}
						}
					}
				}
			}
		}

		this.events.fire(CellManagerEvents.afterCellsValidation, [this.swimlane.id, true, action]);
		return true;
	}
	public getSubHeaderCellIndex(subheaderId: string): number {
		if (!this.swimlane || !arguments.length) return;

		const type = this.getSubHeaderType(subheaderId);
		const cellId = this.getSubHeaderCellId(subheaderId);

		return this.getCellIndex(cellId, type);
	}
	public getSubHeaderCellId(subheaderId: string): Id | undefined {
		if (!this.swimlane || !arguments.length) return;

		const type = this.getSubHeaderType(subheaderId);
		const headers = this.swimlane[type === "col" ? "subHeaderCols" : "subHeaderRows"]?.headers;
		if (!headers?.length) {
			return;
		}
		const index = headers.findIndex(({ id }) => id === subheaderId);

		const pull = [];

		for (let i = 0; i < this.swimlane.layout.length; i++) {
			const rows = this.swimlane.layout[i];
			for (let k = 0; k < rows.length; k++) {
				const cell = rows[k];
				if (((type === "col" && i === 0) || (type === "row" && k === 0)) && !pull.includes(cell)) {
					pull.push(cell);
				}
			}
		}

		return pull[index];
	}
	public getSubHeaderType(subheaderId: string): ICellType | undefined {
		if (!this.swimlane || !arguments.length) return;

		const { subHeaderCols, subHeaderRows } = this.swimlane;
		const isSubHeaderRowEnable = subHeaderRows?.enable && subHeaderRows?.headers?.length;
		const isSubHeaderColEnable = subHeaderCols?.enable && subHeaderCols?.headers.length;
		let rowIndex = -1;
		let colIndex = -1;
		let type: "row" | "col";

		if (isSubHeaderRowEnable) {
			rowIndex = subHeaderRows.headers.findIndex(({ id }) => subheaderId === id);
			if (rowIndex !== -1) type = "row";
		}
		if (isSubHeaderColEnable) {
			colIndex = subHeaderCols.headers.findIndex(({ id }) => subheaderId === id);
			if (colIndex !== -1) type = "col";
		}

		return type;
	}
	public getCellIndex(cellId: Id, type: ICellType): number | undefined {
		if (!this.swimlane || !this.swimlane?.groupChildren?.includes(cellId) || arguments.length < 2) return;

		const { layout } = this.swimlane;

		for (let i = 0; i < layout.length; i++) {
			const rows = layout[i];
			for (let k = 0; k < rows.length; k++) {
				const cell = rows[k];
				if (cell == cellId) {
					return type === "col" ? k : i;
				}
			}
		}
	}
	public getCellId(cellIndex: number, type: ICellType): Id | undefined {
		if (!this.swimlane || arguments.length < 2) return;

		return this.swimlane.layout[type === "row" ? cellIndex : 0][type === "col" ? cellIndex : 0];
	}
	private getSubHeaderIndex(cellId: Id, type: ICellType): number | undefined {
		if (!this.swimlane || !this.swimlane?.groupChildren?.includes(cellId) || arguments.length < 2) return;

		const { layout } = this.swimlane;
		const pull = {};
		let headerIndex = -1;

		for (let i = 0; i < layout.length; i++) {
			const rows = layout[i];
			for (let k = 0; k < rows.length; k++) {
				const cell = rows[k];
				if (!pull[cell]) {
					if ((type === "col" && i === 0) || (type === "row" && k === 0)) {
						headerIndex++;
					}
					pull[cell] = true;
					if (cellId == cell) return headerIndex;
				}
			}
		}
	}
	private getDirectionType(dir: ActionDirection | ICellType): ICellType {
		return dir === "left" || dir === "right" || dir === "col" ? "col" : "row";
	}
	private getUniqueLayout(): number[][] {
		if (!this.swimlane) return;

		const uniqueLayout = [];

		for (let i = 0; i < this.swimlane.layout.length; i++) {
			const row = this.swimlane.layout[i];
			if (!uniqueLayout[i]) uniqueLayout[i] = [];

			for (let k = 0; k < row.length; k++) {
				const cell = row[k];
				if (!uniqueLayout[i].includes(cell)) {
					uniqueLayout[i].push(cell);
				}
			}
		}

		return uniqueLayout;
	}
}
